// src/components/MultiSeriesChart.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import {
    Box,
    Stack,
    Button,
    IconButton,
    Tooltip,
    Divider,
    Typography,
    Popover,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Checkbox,
    List,
    ListSubheader,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { criarFormatadorEixo, extensaoDosValores } from '../utils/eixoNumerico';

const SERIES_COLOR_MAP = {
    F1: '#7600ed',
    F2: '#0028ff',
    F3: '#00d5ff',
    F4: '#1fff00',
    F5: '#b3ff00',
    F6: '#ffdf00',
    F7: '#ff4f00',
    F8: '#ff0000',
    NIR: '#610000',
    RED: '#dc2626',
    GREEN: '#16a34a',
    BLUE: '#2563eb',
    CLEAR: '#737373',
};

const DEFAULT_COLORS = [
    '#2563eb',
    '#16a34a',
    '#dc2626',
    '#eab308',
    '#7c3aed',
    '#0891b2',
    '#f97316',
    '#9d174d',
    '#525252',
];

const SENSOR_CHANNELS = {
    AS7341: ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'NIR', 'CLEAR'],
    APDS9960: ['CLEAR', 'RED', 'GREEN', 'BLUE'],
};

// Metricas cujo sinal DECRESCE com o crescimento microbiano: na turbidimetria, quanto
// mais turvo, menos luz passa — a curva cai. Invertendo o eixo Y (0 no topo) a leitura
// fica coerente com "crescimento": a curva sobe. O eixo continua mostrando os valores
// REAIS, apenas na ordem inversa, e o usuario pode desligar no botao.
const TIPOS_EIXO_INVERTIDO = ['SPECTRAL_TURBIDIMETRY'];

export default function MultiSeriesChart({ data, sensorType, graficoTipo }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const safe = data ?? { labels: [], series: {}, meta: {} };

    const meta = useMemo(() => safe.meta || {}, [safe.meta]);

    // Comeca invertido nas metricas que decrescem; o usuario alterna no botao.
    const invertePorPadrao = TIPOS_EIXO_INVERTIDO.includes(graficoTipo);

    const [eixoInvertido, setEixoInvertido] = useState(invertePorPadrao);
    // Se o usuario troca de aba (tipo de grafico), volta ao padrao daquele tipo.
    useEffect(() => {
        setEixoInvertido(invertePorPadrao);
    }, [invertePorPadrao]);

    const groups = useMemo(() => Object.keys(safe.series ?? {}), [safe.series]);

    const flatSeries = useMemo(() => {
        const allSeries = groups.flatMap((g) =>
            (safe.series[g] ?? []).map((s) => ({
                ...s,
                key: `${g}.${s.name}`,
                label: s.name,
                group: g,
            }))
        );

        if (!sensorType || !SENSOR_CHANNELS[sensorType]) {
            return allSeries;
        }

        const allowedChannels = new Set(SENSOR_CHANNELS[sensorType]);

        return allSeries.filter((series) => {
            const parts = series.label.split('.');
            const channel = parts[parts.length - 1].toUpperCase();
            return allowedChannels.has(channel);
        });
    }, [groups, safe.series, sensorType]);

    const transformedSeriesData = useMemo(() => {
        const labelsInMs = safe.labels.map((ts) => ts * 1000);
        return flatSeries.map((s) => ({
            ...s,
            data: s.data.map((value, index) => [labelsInMs[index], value]),
        }));
    }, [safe.labels, flatSeries]);

    // Ha eixo direito de verdade? Derivado das SERIES, nao do catalogo de rotulos: um
    // nome de eixo nunca pode aparecer sem dado por tras dele.
    const temEixoDireito = useMemo(
        () => flatSeries.some((s) => s?.axis === 'right'),
        [flatSeries]
    );

    const [visible, setVisible] = useState({});
    useEffect(() => {
        setVisible((prev) => {
            const next = {};
            flatSeries.forEach((s) => {
                next[s.key] =
                    prev[s.key] !== undefined
                        ? prev[s.key]
                        : s?.defaultVisible !== false;
            });
            return next;
        });
    }, [flatSeries]);

    const [colorMap, setColorMap] = useState({});
    useEffect(() => {
        setColorMap((prev) => {
            const next = { ...prev };
            let defaultColorIndex = 0;

            flatSeries.forEach((s) => {
                if (next[s.key]) return;

                if (s.color) {
                    next[s.key] = s.color;
                    return;
                }

                let foundColor = null;
                const seriesLabelUpper = s.label.toUpperCase();

                for (const key in SERIES_COLOR_MAP) {
                    if (
                        seriesLabelUpper.endsWith(`.${key}`) ||
                        seriesLabelUpper.endsWith(key)
                    ) {
                        foundColor = SERIES_COLOR_MAP[key];
                        break;
                    }
                }

                if (foundColor) {
                    next[s.key] = foundColor;
                } else {
                    next[s.key] =
                        DEFAULT_COLORS[
                            defaultColorIndex % DEFAULT_COLORS.length
                        ];
                    defaultColorIndex++;
                }
            });

            Object.keys(next).forEach((k) => {
                if (!flatSeries.find((s) => s.key === k)) {
                    delete next[k];
                }
            });

            return next;
        });
    }, [flatSeries]);

    const { maxElapsed, midnightTimestamps } = useMemo(() => {
        if (!safe.labels || safe.labels.length === 0) {
            return { maxElapsed: 0, midnightTimestamps: [] };
        }
        const firstTimestamp = safe.labels[0];
        const lastTimestamp = safe.labels[safe.labels.length - 1];
        const elapsed = (lastTimestamp - firstTimestamp) / 3600;

        const midnights = [];
        let current = dayjs.unix(firstTimestamp).add(1, 'day').startOf('day');
        while (current.unix() <= lastTimestamp) {
            midnights.push(current.valueOf());
            current = current.add(1, 'day');
        }

        return { maxElapsed: elapsed, midnightTimestamps: midnights };
    }, [safe.labels]);

    // Rotulos do eixo Y: compactos ("11,245 mi") para nao comer a largura no celular,
    // com as casas decimais tiradas da faixa das series VISIVEIS de cada eixo — senao
    // uma frequencia que varia poucos milhares de Hz mostra "11,25 mi" em toda marca.
    // O valor cheio continua no tooltip.
    const formatadoresEixo = useMemo(() => {
        const visiveis = transformedSeriesData.filter((s) => visible[s.key]);
        return {
            esquerdo: criarFormatadorEixo(extensaoDosValores(visiveis.filter((s) => s.axis !== 'right'))),
            direito: criarFormatadorEixo(extensaoDosValores(visiveis.filter((s) => s.axis === 'right'))),
        };
    }, [transformedSeriesData, visible]);

    const option = useMemo(() => {
        const separatorColor = theme.palette.mode === 'dark' ? '#8B949E' : '#666';
        const markLineData = midnightTimestamps.map((ts) => ({
            xAxis: ts,
            lineStyle: { color: separatorColor, type: 'dashed' },
            label: {
                formatter: dayjs(ts).format('DD/MM/YYYY'),
                position: 'end',
                fontSize: 10,
                color: separatorColor,
                padding: [0, 0, 15, 0],
            },
        }));

        const seriesWithOptions = transformedSeriesData
            .filter((s) => visible[s.key])
            .map((s, index) => ({
                name: s.label,
                type: 'line',
                showSymbol: false,
                connectNulls: true,
                smooth: false,
                emphasis: { focus: 'series' },
                xAxisIndex: 0,
                yAxisIndex: s.axis === 'right' ? 1 : 0,
                lineStyle: {
                    width: 1.8,
                    color: colorMap[s.key],
                    type: s.dashed ? 'dashed' : 'solid',
                },
                itemStyle: { color: colorMap[s.key] },
                data: s.data,
                markLine:
                    index === 0
                        ? {
                              symbol: 'none',
                              data: markLineData,
                              silent: true,
                          }
                        : undefined,
            }));

        const textPrimary = theme.palette.text.primary;
        const textSecondary = theme.palette.text.secondary;
        const dividerColor = theme.palette.divider;
        const paperBg = theme.palette.background.paper;
        const isDark = theme.palette.mode === 'dark';

        return {
            animation: false,
            textStyle: { color: textPrimary },
            grid: {
                left: isMobile ? '18%' : 80,
                right: isMobile ? '18%' : 80,
                top: 60,
                bottom: 80,
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                backgroundColor: paperBg,
                borderColor: dividerColor,
                textStyle: { color: textPrimary },
            },
            axisPointer: {
                link: [{ xAxisIndex: 'all' }],
                label: { backgroundColor: isDark ? '#30363D' : '#555' },
            },
            xAxis: [
                {
                    type: 'time',
                    position: 'top',
                    axisLabel: {
                        formatter: '{HH}:{mm}',
                        fontSize: isMobile ? 10 : 12,
                        hideOverlap: true,
                        color: textSecondary,
                    },
                    axisLine: { lineStyle: { color: dividerColor } },
                    axisTick: { lineStyle: { color: dividerColor } },
                    name: 'Hora real',
                    nameLocation: 'middle',
                    nameGap: 20,
                    nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                },
                {
                    type: 'value',
                    position: 'bottom',
                    min: 0,
                    max: maxElapsed,
                    axisLabel: {
                        formatter: (value) => `${Math.round(value)}h`,
                        fontSize: isMobile ? 10 : 12,
                        color: textSecondary,
                    },
                    axisLine: { lineStyle: { color: dividerColor } },
                    axisTick: { lineStyle: { color: dividerColor } },
                    name: 'Horas decorridas',
                    nameLocation: 'middle',
                    nameGap: 35,
                    nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                    splitLine: { show: false },
                },
            ],
            yAxis: [
                {
                    // Nome vem do servidor (EixosGrafico, aplicado nos DOIS caminhos
                    // do GraficoService). O front so renderiza o que recebe.
                    name: meta.yAxisLeft ?? '',
                    type: 'value',
                    scale: true,
                    inverse: eixoInvertido,
                    splitLine: {
                        show: true,
                        lineStyle: { color: dividerColor, opacity: 0.6 },
                    },
                    position: 'left',
                    nameLocation: 'middle',
                    nameGap: isMobile ? 38 : 60,
                    nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                    axisLabel: {
                        fontSize: isMobile ? 10 : 12,
                        color: textSecondary,
                        formatter: formatadoresEixo.esquerdo,
                    },
                    axisLine: { lineStyle: { color: dividerColor } },
                    axisTick: { lineStyle: { color: dividerColor } },
                },
                {
                    // Guarda de renderizacao: nao desenha eixo que nenhuma serie usa.
                    // O servidor ja omite o yAxisRight nesse caso, mas a condicao aqui sai
                    // dos DADOS — vale para qualquer origem de ChartResponse.
                    show: temEixoDireito,
                    name: temEixoDireito ? (meta.yAxisRight ?? '') : '',
                    type: 'value',
                    scale: true,
                    inverse: eixoInvertido,
                    splitLine: { show: false },
                    position: 'right',
                    nameLocation: 'middle',
                    nameGap: isMobile ? 38 : 60,
                    nameTextStyle: { fontSize: isMobile ? 10 : 12, color: textSecondary },
                    axisLabel: {
                        fontSize: isMobile ? 10 : 12,
                        color: textSecondary,
                        formatter: formatadoresEixo.direito,
                    },
                    axisLine: { lineStyle: { color: dividerColor } },
                    axisTick: { lineStyle: { color: dividerColor } },
                },
            ],
            dataZoom: [
                { type: 'inside', xAxisIndex: [0, 1], throttle: 100 },
                {
                    type: 'slider',
                    xAxisIndex: [0, 1],
                    height: 18,
                    bottom: 10,
                    textStyle: { color: textSecondary },
                    borderColor: dividerColor,
                    fillerColor: isDark ? 'rgba(88,166,255,0.08)' : 'rgba(14,34,59,0.08)',
                    handleStyle: { color: isDark ? '#58A6FF' : '#0E223B' },
                },
            ],
            series: seriesWithOptions,
        };
    }, [
        transformedSeriesData,
        visible,
        colorMap,
        meta,
        temEixoDireito,
        isMobile,
        maxElapsed,
        midnightTimestamps,
        theme.palette.mode,
        eixoInvertido,
        formatadoresEixo,
    ]);

    const chartRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [colorsAnchorEl, setColorsAnchorEl] = useState(null);
    const [seriesAnchorEl, setSeriesAnchorEl] = useState(null);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleColorsOpen = (event) => {
        setColorsAnchorEl(event.currentTarget);
        handleMenuClose();
    };
    const handleColorsClose = () => setColorsAnchorEl(null);

    const handleSeriesMenuOpen = (event) =>
        setSeriesAnchorEl(event.currentTarget);
    const handleSeriesMenuClose = () => setSeriesAnchorEl(null);

    const handleToggleSeries = (key) => {
        setVisible((v) => ({ ...v, [key]: !v[key] }));
    };

    const handleResetZoom = () => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (inst) inst.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
        handleMenuClose();
    };

    const handleDownload = () => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (!inst) return;
        const url = inst.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: theme.palette.background.default,
        });
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chart.png';
        a.click();
        handleMenuClose();
    };

    const visibleCount = useMemo(
        () => Object.values(visible).filter(Boolean).length,
        [visible]
    );
    const isEmpty = (safe.labels?.length ?? 0) === 0 || flatSeries.length === 0;

    return (
        <Stack sx={{ height: '100%' }}>
            {!isEmpty && (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSeriesMenuOpen}
                        startIcon={<Lucide.ListFilter />}
                    >
                        Séries ({visibleCount}/{flatSeries.length})
                    </Button>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip
                            title={
                                eixoInvertido
                                    ? 'Eixo Y invertido (0 no topo): a curva sobe conforme o crescimento. Clique para ver na orientação original.'
                                    : 'Inverter o eixo Y (0 no topo) para a curva subir conforme o crescimento microbiano.'
                            }
                        >
                            <IconButton
                                size="small"
                                color={eixoInvertido ? 'primary' : 'default'}
                                onClick={() => setEixoInvertido((v) => !v)}
                                aria-label="Inverter eixo Y"
                            >
                                <Lucide.ArrowUpDown />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Mais opções">
                            <IconButton size="small" onClick={handleMenuOpen}>
                                <Lucide.MoreVertical />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            )}

            <Box sx={{ flexGrow: 1, position: 'relative' }}>
                {isEmpty ? (
                    <Stack
                        sx={{ height: '100%', minHeight: 300 }}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography variant="body2" color="text.secondary">
                            Sem dados para exibir.
                        </Typography>
                    </Stack>
                ) : (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    >
                        <ReactECharts
                            ref={chartRef}
                            notMerge
                            lazyUpdate
                            style={{ height: '100%', width: '100%' }}
                            option={option}
                        />
                    </Box>
                )}
            </Box>

            <Popover
                open={Boolean(seriesAnchorEl)}
                anchorEl={seriesAnchorEl}
                onClose={handleSeriesMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <List
                    dense
                    subheader={<ListSubheader>Exibir Séries</ListSubheader>}
                >
                    {flatSeries.map((s) => (
                        <MenuItem
                            key={s.key}
                            onClick={() => handleToggleSeries(s.key)}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Checkbox
                                    edge="start"
                                    checked={visible[s.key] ?? false}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={{
                                        color: colorMap[s.key],
                                        '&.Mui-checked': {
                                            color: colorMap[s.key],
                                        },
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText primary={s.label} />
                        </MenuItem>
                    ))}
                </List>
            </Popover>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleResetZoom}>
                    <ListItemIcon>
                        <Lucide.RotateCcw size={18} />
                    </ListItemIcon>
                    <ListItemText>Resetar Zoom</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownload}>
                    <ListItemIcon>
                        <Lucide.Download size={18} />
                    </ListItemIcon>
                    <ListItemText>Exportar PNG</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleColorsOpen}>
                    <ListItemIcon>
                        <Lucide.Palette size={18} />
                    </ListItemIcon>
                    <ListItemText>Editar Cores</ListItemText>
                </MenuItem>
            </Menu>

            <Popover
                open={Boolean(colorsAnchorEl)}
                anchorEl={colorsAnchorEl}
                onClose={handleColorsClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Stack spacing={1.5} sx={{ p: 2, maxWidth: 320 }}>
                    <Typography variant="subtitle2">
                        Cores das Séries
                    </Typography>
                    {flatSeries.map((s) => (
                        <Stack
                            key={s.key}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: visible[s.key]
                                        ? 'text.primary'
                                        : 'text.disabled',
                                }}
                            >
                                {s.label}
                            </Typography>
                            <input
                                type="color"
                                value={colorMap[s.key] || '#2563eb'}
                                onChange={(e) =>
                                    setColorMap((m) => ({
                                        ...m,
                                        [s.key]: e.target.value,
                                    }))
                                }
                                style={{
                                    width: 32,
                                    height: 24,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    backgroundColor: 'transparent',
                                }}
                                aria-label={`Cor da série ${s.label}`}
                            />
                        </Stack>
                    ))}
                </Stack>
            </Popover>
        </Stack>
    );
}
